// tenantsApi — typed CRUD for /v1/tenants/* (Route 14).
import { apiFetch } from "../../lib/api-client.js";

export type TenantStatus = "active" | "trial" | "suspended";

export interface TenantRow {
  id: string;
  slug: string;
  name: string;
  plan_id: string;
  plan_label: string;
  status: TenantStatus | string;
  brand_primary: string | null;
  brand_secondary: string | null;
  location_count: number;
  created_at: string;
  updated_at: string;
}

export interface TenantListResponse { items: TenantRow[]; correlation_id: string | null }

export class TenantsApiError extends Error {
  status: number; code: string; correlationId: string | null; fields?: string[];
  constructor(status: number, code: string, correlationId: string | null, fields?: string[]) {
    super(code); this.status = status; this.code = code; this.correlationId = correlationId; this.fields = fields;
  }
}
export function friendlyTenantsError(err: unknown): string {
  if (err instanceof TenantsApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) {
      if (err.code === "plan_change_requires_platform_admin") return "更改方案需平台管理員權限。";
      if (err.code === "status_change_requires_platform_admin") return "更改狀態需平台管理員權限。";
      return "您目前的角色不能修改租戶。";
    }
    if (err.status === 404) return "找不到此租戶。";
    if (err.status === 409 && err.code === "slug_taken") return "此 subdomain 已被使用，請換一個。";
    if (err.status === 400 && err.code === "invalid_body") return `欄位填寫不完整：${(err.fields ?? []).join("、")}。`;
    if (err.status === 400 && err.code === "cannot_suspend_platform") return "不能停用平台租戶。";
    return "租戶資料暫時無法存取。";
  }
  return "連線中斷，請檢查網路後再試。";
}
async function asError(r: Response): Promise<TenantsApiError> {
  let code = "request_failed"; let cid: string | null = r.headers.get("x-correlation-id"); let fields: string[] | undefined;
  try { const b = (await r.json()) as { error?: string; correlation_id?: string; fields?: string[] }; code = b.error ?? code; cid = b.correlation_id ?? cid; fields = b.fields; } catch {}
  return new TenantsApiError(r.status, code, cid, fields);
}

export async function listTenants(q?: string | null, status?: TenantStatus | null): Promise<TenantListResponse> {
  const qs = new URLSearchParams();
  if (q) qs.set("q", q);
  if (status) qs.set("status", status);
  const r = await apiFetch(`/v1/tenants${qs.toString() ? `?${qs}` : ""}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as TenantListResponse;
}

export interface CreateTenantBody {
  slug: string; name: string; plan_id: string; status?: TenantStatus;
  brand_primary?: string; brand_secondary?: string; ops_shard?: string;
}
export async function createTenant(b: CreateTenantBody): Promise<{ id: string }> {
  const r = await apiFetch(`/v1/tenants`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(b), credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { id: string };
}
export type PatchTenantBody = Partial<CreateTenantBody>;
export async function patchTenant(id: string, b: PatchTenantBody): Promise<void> {
  const r = await apiFetch(`/v1/tenants/${encodeURIComponent(id)}`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(b), credentials: "include" });
  if (!r.ok) throw await asError(r);
}
export async function suspendTenant(id: string): Promise<void> {
  const r = await apiFetch(`/v1/tenants/${encodeURIComponent(id)}`, { method: "DELETE", credentials: "include" });
  if (!r.ok) throw await asError(r);
}
